/** Redux Packages. */
import { createSlice } from '@reduxjs/toolkit';

/**
 * What the operator sees in the connection badge.
 * - connecting: first connection of this page load; nothing is shown yet.
 * - connected: live; edits from teammates arrive and saves reach the server.
 * - reconnecting: the link dropped; the client keeps retrying without limit.
 * - syncing: the link is back and the open screens are re-reading what was
 *   changed while the tab was cut off.
 */
export const CONNECTION_PHASE = {
    CONNECTING: 'connecting',
    CONNECTED: 'connected',
    RECONNECTING: 'reconnecting',
    SYNCING: 'syncing',
    REFUSED: 'refused',
};

/**
 * What every screen needs to know about the operator's link: whether it is up
 * (socketStatus), what to tell the operator about it (phase), and which connection
 * the rows on screen were loaded on (connectionEpoch) — rows from an earlier
 * connection may be missing teammates' edits and are re-read, rows from the
 * current one are kept so revisiting a page does not download them again.
 * Only state transitions live here; the per-read progress that decides when a
 * sync is over is counted in shared/lib/readTracker so screens are not redrawn
 * on every read.
 */
const initialState = {
    showSuccessAlert: false,
    socketStatus: false,
    phase: CONNECTION_PHASE.CONNECTING,
    connectionEpoch: 0,
};

const socketSlice = createSlice({
    name: 'socketStatus',
    initialState: initialState,
    reducers: {
        /** Sets the link flags directly, for screens and tests that stand in for a real connection. */
        setConnectionStatus: (state, action) => {
            return { ...state, ...action.payload };
        },
        /**
         * The socket is up. A reconnection starts a sync so the operator knows the
         * grid may still show pre-outage data until the re-reads finish; a first
         * connection has nothing to catch up on and is live at once.
         */
        connectionEstablished: (state, action) => {
            const isReconnect = Boolean(action.payload?.isReconnect);
            state.socketStatus = true;
            state.connectionEpoch += 1;
            state.phase = isReconnect ? CONNECTION_PHASE.SYNCING : CONNECTION_PHASE.CONNECTED;
            state.showSuccessAlert = !isReconnect;
        },
        /** The link dropped or an attempt failed; nothing sent from now on reaches the server. */
        connectionLost: (state) => {
            state.socketStatus = false;
            state.showSuccessAlert = false;
            state.phase = CONNECTION_PHASE.RECONNECTING;
        },
        /**
         * The server has turned this tab away again and again (a login the server no
         * longer accepts). Retrying cannot fix that, so the badge tells the operator
         * to sign in again instead of promising a reconnection that will never come.
         */
        connectionRefused: (state) => {
            state.socketStatus = false;
            state.showSuccessAlert = false;
            state.phase = CONNECTION_PHASE.REFUSED;
        },
        /**
         * The screens have caught up after a reconnection (or there was nothing to
         * catch up on): what the operator sees is live again.
         */
        syncSettled: (state) => {
            if (state.phase !== CONNECTION_PHASE.SYNCING) return;
            state.phase = CONNECTION_PHASE.CONNECTED;
            state.showSuccessAlert = true;
        },
        /** The "connected" toast has been on screen long enough. */
        successAlertShown: (state) => {
            state.showSuccessAlert = false;
        },
    },
});

export const {
    setConnectionStatus,
    connectionEstablished,
    connectionLost,
    syncSettled,
    successAlertShown,
    connectionRefused,
} = socketSlice.actions;
export default socketSlice.reducer;
